/**
 * Tenant (organization) resolution from the request host, client-side.
 *
 * The app is a static SPA (CDN-served, no server), so unlike the Next.js
 * reference this runs in the browser at boot. Each org is served at
 * `{slug}.aqi.live`; we derive the slug from `window.location.hostname` so the
 * login flow can bind the session to that org and show its branding.
 *
 * Kinds:
 *   - admin: the reserved super-admin portal (admin.aqi.live), not an org
 *   - org:   a real organization subdomain ({slug}.aqi.live)
 *   - none:  apex / www / reserved-but-not-admin / unknown host -> strict 404
 */

/** Reserved subdomain for the super-admin portal (admin.aqi.live). Not an org. */
export const ADMIN_SLUG = 'admin'

/** Shared zone the org subdomains live under. Constant (not env-driven). */
export const ROOT_DOMAIN = process.env.APP_ROOT_DOMAIN || 'itg.ai'

/**
 * Vanity apex domains that map to a specific org. These are client-branded
 * deployments served at their own domain (e.g. https://aristotle.ai) instead of
 * a `{slug}.itg.ai` subdomain. Keyed by full lowercased hostname.
 */
export const VANITY_ORG_DOMAINS: Record<string, string> = {
  'aristotle.ai': 'aristotle',
  'www.aristotle.ai': 'aristotle',
}

/**
 * Subdomains permanently moved to a vanity domain. Boot redirects these to their
 * new home so the old `{slug}.itg.ai` URL stops serving. Keyed by full hostname.
 */
export const MOVED_TO_VANITY: Record<string, string> = {
  'aristotle.itg.ai': 'https://aristotle.ai',
}

// Local dev convenience: `{slug}.localhost` and `{slug}.lvh.me` resolve to 127.0.0.1.
const DEV_ROOTS = ['localhost', 'lvh.me']

/**
 * Mirror of the backend's RESERVED_ORG_SLUGS
 * (aristotle-backend/src/common/utils/functions.util.ts). These subdomains are
 * infra or other aqi.live products and can never be an org — keep in sync.
 */
export const RESERVED_ORG_SLUGS = new Set<string>([
  // generic / infra
  'www',
  'api',
  'app',
  'admin',
  'mail',
  'cdn',
  'static',
  'assets',
  'auth',
  'login',
  // existing aqi.live products (must not be hijacked by an org slug)
  'aura-ai',
  'course',
  'less-awkward',
  'research',
  'seesolvescale',
  'wonderlings',
  'zbbf',
  'aqi-prod',
])

function leftmostLabel(sub: string): string | null {
  const label = sub.split('.')[0]?.trim()
  if (!label || label === 'www') return null
  return label
}

/**
 * Derive the org/admin slug from a host. Returns the leftmost subdomain label,
 * or null for the apex, `www`, IPs, and any host outside the known roots.
 */
export function getSlugFromHost(host: string | null | undefined): string | null {
  if (!host) return null
  const hostname = host.split(':')[0].trim().toLowerCase()
  if (!hostname) return null

  // Client-branded vanity apex (e.g. aristotle.ai) -> its fixed org slug.
  if (VANITY_ORG_DOMAINS[hostname]) return VANITY_ORG_DOMAINS[hostname]

  for (const dev of DEV_ROOTS) {
    if (hostname === dev) return ADMIN_SLUG
    if (hostname.endsWith(`.${dev}`)) {
      return leftmostLabel(hostname.slice(0, -`.${dev}`.length))
    }
  }

  if (hostname === ROOT_DOMAIN) return null
  if (hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    return leftmostLabel(hostname.slice(0, -`.${ROOT_DOMAIN}`.length))
  }

  // App Runner default domain, IPs, unknown hosts -> no tenant.
  return null
}

export type TenantKind = 'admin' | 'org' | 'none'

/**
 * Classify a resolved slug. A reserved non-admin slug is `none` (another
 * product's subdomain, never our org) so strict mode 404s it.
 */
export function resolveTenantKind(slug: string | null): TenantKind {
  if (!slug) return 'none'
  if (slug === ADMIN_SLUG) return 'admin'
  if (RESERVED_ORG_SLUGS.has(slug)) return 'none'
  return 'org'
}
